import React, { useEffect, useMemo, useState } from 'react';
import {
  Bell, Plus, Trash2, RefreshCw, Zap, X, AlertCircle, CheckCircle2, BellRing, Power
} from 'lucide-react';
import { apiCallJson } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

// Mirrors backend alert-rules-service METRICS / OPERATORS / TIMEFRAMES
const METRIC_OPTIONS = [
  { value: 'cost', label: 'Spend', unit: '€' },
  { value: 'conversions', label: 'Conversions', unit: '' },
  { value: 'cpa', label: 'CPA', unit: '€' },
  { value: 'roas', label: 'ROAS', unit: 'x' },
  { value: 'ctr', label: 'CTR', unit: '%' },
  { value: 'conversion_rate', label: 'Conversion rate', unit: '%' },
  { value: 'clicks', label: 'Clicks', unit: '' },
  { value: 'impressions', label: 'Impressions', unit: '' }
];

const OPERATOR_OPTIONS = [
  { value: 'above', label: 'is above' },
  { value: 'below', label: 'is below' },
  { value: 'increase_pct', label: 'increased by % (vs previous period)' },
  { value: 'decrease_pct', label: 'decreased by % (vs previous period)' }
];

const TIMEFRAME_OPTIONS = [
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' }
];

const TEMPLATES = [
  { name: 'ROAS dropped below target', metric: 'roas', operator: 'below', threshold: 3, timeframe: 'last_7_days' },
  { name: 'CPA above target', metric: 'cpa', operator: 'above', threshold: 50, timeframe: 'last_7_days' },
  { name: 'Spend spiked +30% WoW', metric: 'cost', operator: 'increase_pct', threshold: 30, timeframe: 'last_7_days' },
  { name: 'Conversions dropped 25%', metric: 'conversions', operator: 'decrease_pct', threshold: 25, timeframe: 'last_7_days' }
];

function metricMeta(value) {
  return METRIC_OPTIONS.find(m => m.value === value) || { label: value, unit: '' };
}

function operatorLabel(op) {
  return OPERATOR_OPTIONS.find(o => o.value === op)?.label || op;
}

function timeframeLabel(tf) {
  return TIMEFRAME_OPTIONS.find(t => t.value === tf)?.label || tf;
}

function isPctOperator(op) {
  return op === 'increase_pct' || op === 'decrease_pct';
}

function formatRelative(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

const EMPTY_FORM = { name: '', metric: 'roas', operator: 'below', threshold: '', timeframe: 'last_7_days' };

export default function AlertRules() {
  const { theme } = useTheme();
  const c = theme.colors;

  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [toast, setToast] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiCallJson('/api/alert-rules');
      if (data?.success) {
        setRules(data.rules || []);
        setEvents(data.events || []);
      } else {
        setError(data?.error || 'Could not load alert rules');
      }
    } catch (err) {
      setError(err.message || 'Could not load alert rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const openCreate = (template) => {
    setEditingId(null);
    setTestResult(null);
    setForm(template ? { ...EMPTY_FORM, ...template, threshold: String(template.threshold) } : EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setTestResult(null);
    setForm({
      name: rule.name,
      metric: rule.metric,
      operator: rule.operator,
      threshold: String(rule.threshold),
      timeframe: rule.timeframe
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setEditingId(null);
    setTestResult(null);
  };

  const validForm = form.name.trim() && form.threshold !== '' && !isNaN(Number(form.threshold));

  const save = async () => {
    if (!validForm) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        metric: form.metric,
        operator: form.operator,
        threshold: Number(form.threshold),
        timeframe: form.timeframe
      };
      const data = editingId
        ? await apiCallJson(`/api/alert-rules/${editingId}`, { method: 'PUT', body })
        : await apiCallJson('/api/alert-rules', { method: 'POST', body });
      if (data?.success) {
        setToast({ type: 'success', message: editingId ? 'Rule updated' : 'Rule created' });
        closeForm();
        load();
      } else {
        setToast({ type: 'error', message: data?.error || 'Could not save rule' });
      }
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Could not save rule' });
    } finally {
      setSaving(false);
    }
  };

  const testRule = async () => {
    if (!validForm) return;
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiCallJson('/api/alert-rules/test', {
        method: 'POST',
        body: {
          metric: form.metric,
          operator: form.operator,
          threshold: Number(form.threshold),
          timeframe: form.timeframe
        },
        timeout: 45000
      });
      if (data?.success) {
        setTestResult({ ok: true, wouldFire: data.wouldFire, message: data.message, value: data.currentValueFormatted });
      } else {
        setTestResult({ ok: false, message: data?.error || 'Test failed' });
      }
    } catch (err) {
      setTestResult({ ok: false, message: err.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const toggleRule = async (rule) => {
    setRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: !r.enabled } : r));
    try {
      await apiCallJson(`/api/alert-rules/${rule.id}`, { method: 'PUT', body: { enabled: !rule.enabled } });
    } catch {
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: rule.enabled } : r));
      setToast({ type: 'error', message: 'Could not update rule' });
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete alert rule "${rule.name}"?`)) return;
    try {
      const data = await apiCallJson(`/api/alert-rules/${rule.id}`, { method: 'DELETE' });
      if (data?.success) {
        setRules(prev => prev.filter(r => r.id !== rule.id));
        setToast({ type: 'success', message: 'Rule deleted' });
      } else {
        setToast({ type: 'error', message: 'Could not delete rule' });
      }
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Could not delete rule' });
    }
  };

  const ruleSummary = (rule) => {
    const m = metricMeta(rule.metric);
    if (isPctOperator(rule.operator)) {
      const dir = rule.operator === 'increase_pct' ? 'increases' : 'drops';
      return `Alert when ${m.label} ${dir} by ${rule.threshold}% (${timeframeLabel(rule.timeframe).toLowerCase()})`;
    }
    const thr = m.unit === '€' ? `€${rule.threshold}` : m.unit === 'x' ? `${rule.threshold}x` : m.unit === '%' ? `${rule.threshold}%` : rule.threshold;
    return `Alert when ${m.label} ${operatorLabel(rule.operator)} ${thr} (${timeframeLabel(rule.timeframe).toLowerCase()})`;
  };

  const enabledCount = useMemo(() => rules.filter(r => r.enabled).length, [rules]);

  const inputStyle = {
    padding: '10px 12px', borderRadius: 10, width: '100%', boxSizing: 'border-box',
    border: `1px solid ${c.border.primary}`, background: c.bg.tertiary,
    color: c.text.primary, fontSize: 14, outline: 'none'
  };
  const labelStyle = { display: 'block', color: c.text.secondary, fontSize: 13, fontWeight: 600, marginBottom: 6 };
  const card = { background: c.bg.secondary, border: `1px solid ${c.border.primary}`, borderRadius: 16 };

  return (
    <div style={{ padding: 40, maxWidth: 1000, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Bell size={24} color="#fff" />
          </div>
          <div>
            <h1 style={{ color: c.text.primary, fontSize: 26, fontWeight: 700, margin: 0 }}>Alert Rules</h1>
            <p style={{ color: c.text.secondary, fontSize: 14, margin: '4px 0 0' }}>
              Get notified the moment your account crosses a threshold you care about. Checked automatically every few hours.
            </p>
          </div>
        </div>
        <button
          onClick={() => openCreate(null)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px',
            borderRadius: 10, border: 'none', background: c.brand.primary, color: '#fff',
            fontSize: 14, fontWeight: 600, cursor: 'pointer'
          }}
        >
          <Plus size={16} /> New rule
        </button>
      </div>

      {error && (
        <div style={{ ...card, padding: 16, marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center', borderColor: 'rgba(239,68,68,0.4)' }}>
          <AlertCircle size={18} color="#ef4444" />
          <span style={{ color: c.text.primary, fontSize: 14 }}>{error}</span>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: c.text.secondary }}>
          <RefreshCw size={36} className="spin" style={{ marginBottom: 12 }} />
          <p>Loading alert rules…</p>
        </div>
      ) : (
        <>
          {/* Templates / empty state */}
          {rules.length === 0 && (
            <div style={{ ...card, padding: 28, marginBottom: 24 }}>
              <h3 style={{ color: c.text.primary, margin: '0 0 6px' }}>Start with a template</h3>
              <p style={{ color: c.text.secondary, fontSize: 14, margin: '0 0 18px' }}>
                Pick a common rule below, or build your own. You can edit thresholds any time.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                {TEMPLATES.map(t => (
                  <button
                    key={t.name}
                    onClick={() => openCreate(t)}
                    style={{
                      textAlign: 'left', padding: 16, borderRadius: 12, cursor: 'pointer',
                      border: `1px solid ${c.border.primary}`, background: c.bg.tertiary
                    }}
                  >
                    <div style={{ color: c.text.primary, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{t.name}</div>
                    <div style={{ color: c.text.secondary, fontSize: 12 }}>{timeframeLabel(t.timeframe)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Rules list */}
          {rules.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h3 style={{ color: c.text.primary, fontSize: 16, fontWeight: 700, margin: 0 }}>
                  Your rules ({enabledCount} active)
                </h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {rules.map(rule => (
                  <div key={rule.id} style={{ ...card, padding: 16, display: 'flex', gap: 14, alignItems: 'center' }}>
                    <button
                      onClick={() => toggleRule(rule)}
                      title={rule.enabled ? 'Disable' : 'Enable'}
                      style={{
                        width: 40, height: 24, borderRadius: 999, flexShrink: 0, border: 'none', cursor: 'pointer',
                        background: rule.enabled ? c.status.success : c.bg.tertiary,
                        position: 'relative', transition: 'background 0.2s'
                      }}
                    >
                      <span style={{
                        position: 'absolute', top: 2, left: rule.enabled ? 18 : 2,
                        width: 20, height: 20, borderRadius: '50%', background: '#fff',
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }} />
                    </button>

                    <div style={{ flex: 1, minWidth: 0, opacity: rule.enabled ? 1 : 0.55 }}>
                      <div style={{ color: c.text.primary, fontSize: 15, fontWeight: 600 }}>{rule.name}</div>
                      <div style={{ color: c.text.secondary, fontSize: 13, marginTop: 2 }}>{ruleSummary(rule)}</div>
                      {rule.lastTriggeredAt && (
                        <div style={{ color: '#f59e0b', fontSize: 12, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <BellRing size={12} /> Last fired {formatRelative(rule.lastTriggeredAt)}
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button onClick={() => openEdit(rule)} style={{
                        padding: '7px 12px', borderRadius: 8, border: `1px solid ${c.border.primary}`,
                        background: c.bg.tertiary, color: c.text.primary, fontSize: 13, fontWeight: 600, cursor: 'pointer'
                      }}>Edit</button>
                      <button onClick={() => deleteRule(rule)} title="Delete" style={{
                        padding: '7px 10px', borderRadius: 8, border: `1px solid ${c.border.primary}`,
                        background: c.bg.tertiary, color: '#ef4444', cursor: 'pointer', display: 'inline-flex', alignItems: 'center'
                      }}><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent triggers */}
          {events.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ color: c.text.primary, fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>Recently triggered</h3>
              <div style={{ ...card, padding: 8 }}>
                {events.slice(0, 10).map(ev => (
                  <div key={ev.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 12px' }}>
                    <BellRing size={16} color="#f59e0b" style={{ marginTop: 2, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ color: c.text.primary, fontSize: 13 }}>{ev.message}</div>
                      <div style={{ color: c.text.tertiary, fontSize: 12, marginTop: 2 }}>{formatRelative(ev.createdAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Create / edit modal */}
      {showForm && (
        <div
          onClick={closeForm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            ...card, width: '100%', maxWidth: 520, padding: 28, maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ color: c.text.primary, fontSize: 20, fontWeight: 700, margin: 0 }}>
                {editingId ? 'Edit rule' : 'New alert rule'}
              </h2>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.text.secondary }}>
                <X size={22} />
              </button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Rule name</label>
              <input
                style={inputStyle}
                value={form.name}
                placeholder="e.g. ROAS dropped below target"
                onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setTestResult(null); }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Metric</label>
                <select
                  style={inputStyle}
                  value={form.metric}
                  onChange={e => { setForm(f => ({ ...f, metric: e.target.value })); setTestResult(null); }}
                >
                  {METRIC_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Condition</label>
                <select
                  style={inputStyle}
                  value={form.operator}
                  onChange={e => { setForm(f => ({ ...f, operator: e.target.value })); setTestResult(null); }}
                >
                  {OPERATOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>{isPctOperator(form.operator) ? 'Percentage (%)' : `Threshold (${metricMeta(form.metric).unit || 'value'})`}</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={form.threshold}
                  placeholder={isPctOperator(form.operator) ? '30' : '3'}
                  onChange={e => { setForm(f => ({ ...f, threshold: e.target.value })); setTestResult(null); }}
                />
              </div>
              <div>
                <label style={labelStyle}>Timeframe</label>
                <select
                  style={inputStyle}
                  value={form.timeframe}
                  onChange={e => { setForm(f => ({ ...f, timeframe: e.target.value })); setTestResult(null); }}
                >
                  {TIMEFRAME_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            {/* Test result */}
            {testResult && (
              <div style={{
                padding: 14, borderRadius: 10, marginBottom: 16,
                background: testResult.ok
                  ? (testResult.wouldFire ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.12)')
                  : 'rgba(239,68,68,0.12)',
                border: `1px solid ${testResult.ok ? (testResult.wouldFire ? 'rgba(245,158,11,0.4)' : 'rgba(16,185,129,0.4)') : 'rgba(239,68,68,0.4)'}`,
                display: 'flex', gap: 10, alignItems: 'flex-start'
              }}>
                {testResult.ok
                  ? (testResult.wouldFire ? <BellRing size={18} color="#f59e0b" /> : <CheckCircle2 size={18} color="#10b981" />)
                  : <AlertCircle size={18} color="#ef4444" />}
                <span style={{ color: c.text.primary, fontSize: 13 }}>{testResult.message}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: 4 }}>
              <button
                onClick={testRule}
                disabled={!validForm || testing}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 16px', borderRadius: 10,
                  border: `1px solid ${c.border.primary}`, background: c.bg.tertiary, color: c.text.primary,
                  fontSize: 14, fontWeight: 600, cursor: (!validForm || testing) ? 'not-allowed' : 'pointer',
                  opacity: (!validForm || testing) ? 0.6 : 1
                }}
              >
                {testing ? <RefreshCw size={15} className="spin" /> : <Zap size={15} />} Test against live data
              </button>
              <button
                onClick={save}
                disabled={!validForm || saving}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 10,
                  border: 'none', background: c.brand.primary, color: '#fff',
                  fontSize: 14, fontWeight: 700, cursor: (!validForm || saving) ? 'not-allowed' : 'pointer',
                  opacity: (!validForm || saving) ? 0.6 : 1
                }}
              >
                {saving ? <RefreshCw size={15} className="spin" /> : <CheckCircle2 size={15} />} {editingId ? 'Save changes' : 'Create rule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1100,
          background: toast.type === 'success' ? '#10b981' : '#ef4444',
          color: '#fff', padding: '12px 18px', borderRadius: 10,
          fontSize: 14, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: 8, maxWidth: 360
        }}>
          {toast.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          {toast.message}
        </div>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
